// src/sectors/pilates/members/modal/detail/tabs/PaymentsTab.jsx
import React from 'react';
import Icon from '@mui/material/Icon';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Divider from '@mui/material/Divider';
import useMediaQuery from '@mui/material/useMediaQuery';
import { alpha, useTheme as useMuiTheme } from '@mui/material/styles';

import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDButton from 'components/MDButton';

import { SoftCard } from 'shared/ui/soft/SoftCard';
import { PillIcon } from 'shared/ui/soft/PillIcon';
import { RowItem } from 'shared/ui/soft/RowItem';
import { TabPanel } from 'shared/ui/soft/TabPanel';

import { dateTR, payMethodLabel } from '../utils/memberDetailUtils';
import { money } from 'sectors/pilates/members/components/paymentReceive/ui';

// ---------------- helpers ----------------
const MONTHS_TR = [
  'Ocak',
  'Şubat',
  'Mart',
  'Nisan',
  'Mayıs',
  'Haziran',
  'Temmuz',
  'Ağustos',
  'Eylül',
  'Ekim',
  'Kasım',
  'Aralık',
];

function parseLocalDateString(s) {
  if (!s || typeof s !== 'string') return null;
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return null;
  const y = Number(m[1]);
  const mo = Number(m[2]);
  const da = Number(m[3]);
  const dt = new Date(y, mo - 1, da);
  return Number.isNaN(dt.getTime()) ? null : dt;
}

function dMonYYYY_TR_fromYmd(ymd) {
  const d = parseLocalDateString(ymd);
  if (!d) return null;
  return `${d.getDate()} ${MONTHS_TR[d.getMonth()]} ${d.getFullYear()}`;
}

function paymentPurpose(p) {
  const v = String(p?.purpose || '')
    .toUpperCase()
    .trim();
  if (v === 'PLAN') return 'PLAN';
  if (v === 'SESSION') return 'SESSION';
  return 'GENERAL';
}

function isEarlyPlanPayment(p) {
  const note = String(p?.note || '').toLowerCase();
  return note.includes('erken ödeme');
}

function planStartLabel(p) {
  const ymd = p?.planStartAt || p?.memberPlanStartAt || p?.startAt || p?.fromPlanStartAt || null;
  return dMonYYYY_TR_fromYmd(ymd);
}

function whoLabel(p) {
  return p?.receivedByName || '—';
}

function chipByPurpose(p) {
  const purpose = paymentPurpose(p);
  if (purpose === 'PLAN') return { label: 'Paket', color: 'warning' };
  if (purpose === 'SESSION') return { label: 'Seans', color: 'info' };
  return { label: 'Diğer', color: 'success' };
}

function toneByPurpose(p) {
  const purpose = paymentPurpose(p);
  if (purpose === 'PLAN') return 'amber';
  if (purpose === 'SESSION') return 'blue';
  return 'teal';
}

function buildLine({ memberLabel, p, theme }) {
  const purpose = paymentPurpose(p);
  const method = payMethodLabel(p?.method);
  const packedname = p?.planNameSnapshot || '—';
  const paidDate = p?.paidDate || '—';
  const who = whoLabel(p);

  const note = (p?.note || '').trim();
  const hasNote = !!note;

  const whoSpan = (
    <span style={{ fontWeight: 800, color: theme?.palette?.text?.primary }}>{who}</span>
  );

  if (purpose === 'PLAN') {
    const start = planStartLabel(p);
    const early = isEarlyPlanPayment(p) || !start;
    const planPart = start ? `${start}  ${packedname} paket` : `paket ödemesi (başlangıç yok)`;
    const kindPart = hasNote ? ` - ${note}` : '';
    return (
      <span>
        <b>{planPart}</b>
        {early ? '' : ''}
        {kindPart} • {method} • Alan: {whoSpan}
      </span>
    );
  }

  const whoPrefix = memberLabel ? `${memberLabel} ` : '';

  if (purpose === 'SESSION') {
    const kindPart = hasNote ? ` - ${note}` : '';
    return (
      <span>
        {whoPrefix}
        <b>seans ödemesi</b>
        {kindPart} • {paidDate} • {method} • Alan: {whoSpan}
      </span>
    );
  }

  const kindPart = hasNote ? ` - ${note}` : '';
  return (
    <span>
      {whoPrefix}
      <b>diğer ödeme</b>
      {kindPart} • {paidDate} • {method} • Alan: {whoSpan}
    </span>
  );
}

function refTypeLabel(t) {
  const v = String(t || '').toUpperCase();
  if (v === 'MEMBER_PLAN') return 'Paket';
  if (v === 'PAYMENT') return 'Ödeme';
  if (v === 'SESSION') return 'Seans';
  if (v === 'MEMBER') return 'Üyelik';
  if (v === 'MANUAL') return 'Manuel';
  return 'İşlem';
}

function actionLabel({ remainingTry, debitTry, creditTry }) {
  if (Number(remainingTry) > 0) return 'Borç Eklendi';
  if (Number(creditTry) > 0) return 'Ödeme alındı';
  if (Number(debitTry) > 0) return 'Borç eklendi';
  return 'Güncelleme';
}

function pickDisplayTry({ remainingTry, debitTry, creditTry }) {
  const r = Number(remainingTry || 0);
  const d = Number(debitTry || 0);
  const c = Number(creditTry || 0);
  return r > 0 ? r : d > 0 ? d : c;
}

function buildDebtTitle(it) {
  const remainingTry = Number(it?.remainingTry || 0);
  const debitTry = Number(it?.debitTry || 0);
  const creditTry = Number(it?.creditTry || 0);

  const base = actionLabel({ remainingTry, debitTry, creditTry });
  const amount = money(pickDisplayTry({ remainingTry, debitTry, creditTry }));

  return `${base} • ${amount}`;
}

function buildDebtSubtitle(it) {
  const typeTxt = refTypeLabel(it?.refType);
  const noTxt = it?.refId ? `#${it.refId}` : '';
  const desc = (it?.description || '').trim();
  return `${typeTxt} ${noTxt}${desc ? ` • ${desc}` : ''}`.trim();
}

// ---------------- small UI atoms ----------------
function MiniStat({ icon, tone = 'blue', label, value, hint }) {
  return (
    <MDBox
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.9,
        borderRadius: '12px',
        px: 0.8,
        py: 0.6,
        minWidth: 0,
      }}
    >
      <PillIcon icon={icon} tone={tone} />

      <MDBox sx={{ minWidth: 0 }}>
        <MDTypography
          fontWeight="bold"
          sx={{
            fontSize: { xs: '0.92rem', sm: '0.98rem' },
            lineHeight: 1.05,
          }}
        >
          {value}
        </MDTypography>

        <MDBox sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
          <MDTypography
            variant="caption"
            sx={{
              fontSize: { xs: '0.68rem', sm: '0.72rem' },
              opacity: 0.7,
              fontWeight: 800,
            }}
          >
            {label}
          </MDTypography>

          {hint && (
            <Tooltip title={hint}>
              <Icon sx={{ fontSize: 15, opacity: 0.5 }}>info</Icon>
            </Tooltip>
          )}
        </MDBox>
      </MDBox>
    </MDBox>
  );
}

function SectionHeader({ icon, tone, title, subtitle, right, typo }) {
  return (
    <MDBox
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 1,
        minWidth: 0,
      }}
    >
      <MDBox display="flex" alignItems="center" gap={1} sx={{ minWidth: 0 }}>
        <PillIcon icon={icon} tone={tone} />
        <MDBox sx={{ minWidth: 0 }}>
          <MDTypography variant="h6" fontWeight="bold" sx={{ ...typo.title, lineHeight: 1.12 }}>
            {title}
          </MDTypography>
          <MDTypography
            variant="caption"
            sx={{
              ...typo.sub,
              color: 'text.secondary',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {subtitle}
          </MDTypography>
        </MDBox>
      </MDBox>

      {right}
    </MDBox>
  );
}

function EmptyState({ title, subtitle, theme, dividerColor, tone = 'info' }) {
  return (
    <MDBox
      sx={{
        p: 1.25,
        borderRadius: '16px',
        border: `1px solid ${dividerColor}`,
        background: alpha(theme.palette[tone].main, theme.palette.mode === 'dark' ? 0.06 : 0.03),
      }}
    >
      <MDTypography variant="button" fontWeight="bold">
        {title}
      </MDTypography>
      <MDTypography variant="caption" sx={{ color: 'text.secondary' }}>
        {subtitle}
      </MDTypography>
    </MDBox>
  );
}

function PaymentRow({
  p,
  theme,
  isMobile,
  typo,
  chipSx,
  memberLabel,
  canEdit,
  canDelete,
  onEditPayment,
  onDeletePayment,
  openMenu,
}) {
  const amountTry = Number(p?.amountTry || 0);
  const title = money(amountTry);
  const purposeChip = chipByPurpose(p);
  const meta = buildLine({ memberLabel, p, theme });

  const key = p?.id ?? `${p?.paidDate}-${p?.createdAt}-${amountTry}`;

  return (
    <RowItem
      key={key}
      icon="payments"
      tone={toneByPurpose(p)}
      title={
        <MDBox display="flex" alignItems="center" gap={1} sx={{ minWidth: 0 }}>
          <MDTypography fontWeight="bold" sx={{ ...typo.rowTitle, lineHeight: 1.1 }}>
            {title}
          </MDTypography>

          <Chip
            size="small"
            label={purposeChip.label}
            color={purposeChip.color}
            variant="outlined"
            sx={{
              borderRadius: '999px',
              fontWeight: 900,
              '& .MuiChip-label': { fontSize: typo.chip.fontSize },
            }}
          />
        </MDBox>
      }
      subtitle={
        <MDBox sx={{ ...typo.rowSub, color: 'text.secondary', lineHeight: 1.25 }}>{meta}</MDBox>
      }
      right={
        <MDBox display="flex" alignItems="center" gap={0.5}>
          <Chip size="small" label={dateTR(p?.createdAt)} variant="outlined" sx={chipSx} />

          {!isMobile ? (
            <>
              <Tooltip title="Ödemeyi düzenle">
                <span>
                  <IconButton size="small" onClick={() => onEditPayment?.(p)} disabled={!canEdit}>
                    <Icon fontSize="small">edit</Icon>
                  </IconButton>
                </span>
              </Tooltip>

              <Tooltip title="Ödemeyi sil">
                <span>
                  <IconButton
                    size="small"
                    onClick={() => onDeletePayment?.(p)}
                    disabled={!canDelete}
                  >
                    <Icon fontSize="small">delete</Icon>
                  </IconButton>
                </span>
              </Tooltip>
            </>
          ) : (
            <IconButton size="small" onClick={(e) => openMenu(e, p)}>
              <Icon fontSize="small">more_vert</Icon>
            </IconButton>
          )}
        </MDBox>
      }
    />
  );
}

function DebtRow({ it, idx, typo, chipSx }) {
  const creditTry = Number(it?.creditTry || 0);
  const remainingTry = Number(it?.remainingTry || 0);

  const status =
    remainingTry > 0
      ? { label: 'Borç', tone: 'amber', color: 'warning' }
      : creditTry > 0
      ? { label: 'Ödeme', tone: 'teal', color: 'success' }
      : { label: 'Düzeltme', tone: 'blue', color: 'info' };

  const titleText = buildDebtTitle(it);
  const subtitleText = buildDebtSubtitle(it);

  return (
    <RowItem
      key={`${it?.refType}-${it?.refId}-${idx}`}
      icon="account_balance_wallet"
      tone={status.tone}
      title={
        <MDBox display="flex" alignItems="center" gap={1}>
          <MDTypography fontWeight="bold" sx={{ ...typo.rowTitle, lineHeight: 1.1 }}>
            {titleText}
          </MDTypography>
          <Chip
            size="small"
            label={status.label}
            color={status.color}
            variant="outlined"
            sx={{
              borderRadius: '999px',
              fontWeight: 900,
              '& .MuiChip-label': { fontSize: typo.chip.fontSize },
            }}
          />
        </MDBox>
      }
      subtitle={
        <MDBox sx={{ ...typo.rowSub, color: 'text.secondary', lineHeight: 1.25 }}>
          {subtitleText}
        </MDBox>
      }
      right={
        <Chip
          size="small"
          label={it?.lastOccurredAt ? dateTR(it.lastOccurredAt) : '—'}
          variant="outlined"
          sx={chipSx}
        />
      }
    />
  );
}

// ---------------- component ----------------
export function PaymentsTab({
  tab,
  theme: themeProp,
  debt,
  paymentsState,
  memberLabel,
  onEditPayment,
  onDeletePayment,
  debtSummary,
  debtLoading,
  debtErr,
  onRefreshDebt,
}) {
  const muiTheme = useMuiTheme();
  const theme = themeProp || muiTheme;
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const { loading, err, payments = [], fetchPayments } = paymentsState || {};

  const totalPaidTry = Array.isArray(payments)
    ? payments.reduce((s, p) => s + Number(p?.amountTry || 0), 0)
    : 0;

  const lastPayment = Array.isArray(payments) && payments.length > 0 ? payments[0] : null;

  const dividerColor = theme?.palette?.divider || 'rgba(0,0,0,.12)';
  const errColor = theme?.palette?.error?.main || '#d32f2f';

  const typo = {
    title: { fontSize: isMobile ? '0.98rem' : '1.05rem' },
    sub: { fontSize: isMobile ? '0.72rem' : '0.80rem' },
    rowTitle: { fontSize: isMobile ? '0.92rem' : '0.98rem' },
    rowSub: { fontSize: isMobile ? '0.70rem' : '0.78rem' },
    chip: { fontSize: isMobile ? '0.68rem' : '0.72rem' },
  };

  const chipSx = {
    borderRadius: '999px',
    fontWeight: 900,
    borderColor: alpha(theme?.palette?.divider || '#000', 0.55),
    background: alpha(theme?.palette?.background?.paper || '#fff', 0.45),
    '& .MuiChip-label': { px: 1.0, fontSize: typo.chip.fontSize },
  };

  // Mobile action menu
  const [menu, setMenu] = React.useState({ anchorEl: null, payment: null });
  const menuOpen = Boolean(menu.anchorEl);
  const openMenu = (e, p) => setMenu({ anchorEl: e.currentTarget, payment: p });
  const closeMenu = () => setMenu({ anchorEl: null, payment: null });

  const canEdit = typeof onEditPayment === 'function';
  const canDelete = typeof onDeletePayment === 'function';

  const netDebt = Number(debtSummary?.balanceTry ?? debt ?? 0);

  return (
    <TabPanel value={tab} index={1}>
      <MDBox sx={{ display: 'grid', gap: 1.1 }}>
        {/* ✅ TEK CONTAINER: CRM-style */}
        <SoftCard sx={{ p: isMobile ? 1.1 : 1.5 }}>
          {/* ===== FLAT STATS STRIP ===== */}
          <MDBox
            sx={{
              display: 'grid',
              gap: 0.5,
              gridTemplateColumns: {
                xs: 'repeat(2, minmax(0, 1fr))',
                sm: 'repeat(4, minmax(0, 1fr))',
              },
              mb: 1,
            }}
          >
            <MiniStat
              tone="teal"
              icon="receipt_long"
              label="Toplam Kayıt"
              value={`${Array.isArray(payments) ? payments.length : 0}`}
              hint="Ödeme hareketi"
            />
            <MiniStat
              tone="amber"
              icon="payments"
              label="Toplam Alınan"
              value={money(totalPaidTry)}
              hint="Bu üyeden alınan"
            />
            <MiniStat
              tone={netDebt > 0 ? 'amber' : 'teal'}
              icon="account_balance_wallet"
              label="Borç"
              value={money(netDebt)}
              hint="Net bakiye"
            />
            <MiniStat
              tone="blue"
              icon="event"
              label={isMobile ? 'Son' : 'Son Ödeme'}
              value={lastPayment ? dateTR(lastPayment?.createdAt) : '—'}
              hint={
                lastPayment
                  ? `${payMethodLabel(lastPayment?.method)} • Alan: ${whoLabel(lastPayment)}`
                  : null
              }
            />
          </MDBox>

          <Divider sx={{ my: 1.2 }} />

          {/* ===== ÖDEMELER ===== */}
          <SectionHeader
            icon="payments"
            tone="teal"
            title="Alınan Ödemeler"
            subtitle="Tahsilatlar ve ödeme hareketleri"
            typo={typo}
            right={
              <Tooltip title="Yenile">
                <span>
                  <IconButton
                    size="small"
                    onClick={fetchPayments}
                    disabled={loading || !fetchPayments}
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: '10px',
                      color: theme.palette.success.main,
                      background: alpha(theme.palette.success.main, 0.1),
                      '&:hover': {
                        background: alpha(theme.palette.success.main, 0.18),
                      },
                    }}
                  >
                    {loading ? (
                      <CircularProgress size={16} sx={{ color: theme.palette.success.main }} />
                    ) : (
                      <Icon sx={{ fontSize: 18 }}>refresh</Icon>
                    )}
                  </IconButton>
                </span>
              </Tooltip>
            }
          />

          {loading ? (
            <MDBox sx={{ py: 2.5, display: 'grid', placeItems: 'center' }}>
              <CircularProgress size={24} />
              <MDTypography variant="caption" sx={{ mt: 1, color: 'text.secondary', ...typo.sub }}>
                Ödemeler yükleniyor…
              </MDTypography>
            </MDBox>
          ) : err ? (
            <MDBox
              sx={{
                p: 1.25,
                borderRadius: '16px',
                border: `1px solid ${dividerColor}`,
                background: alpha(
                  theme.palette.error.main,
                  theme.palette.mode === 'dark' ? 0.08 : 0.04
                ),
              }}
            >
              <MDTypography variant="button" fontWeight="bold" sx={{ color: errColor }}>
                {err}
              </MDTypography>
              <MDTypography variant="caption" sx={{ color: 'text.secondary', ...typo.sub }}>
                Tekrar denemek için “Yenile”ye bas.
              </MDTypography>
            </MDBox>
          ) : !Array.isArray(payments) || payments.length === 0 ? (
            <EmptyState
              title="Henüz ödeme yok"
              subtitle="İlk ödeme alındığında burada listelenecek."
              theme={theme}
              dividerColor={dividerColor}
              tone="info"
            />
          ) : (
            <MDBox sx={{ display: 'grid', gap: 0.9, mt: 0.5 }}>
              {payments.map((p) => (
                <PaymentRow
                  key={p?.id ?? `${p?.paidDate}-${p?.createdAt}-${p?.amountTry}`}
                  p={p}
                  theme={theme}
                  isMobile={isMobile}
                  typo={typo}
                  chipSx={chipSx}
                  memberLabel={memberLabel}
                  canEdit={canEdit}
                  canDelete={canDelete}
                  onEditPayment={onEditPayment}
                  onDeletePayment={onDeletePayment}
                  openMenu={openMenu}
                />
              ))}
            </MDBox>
          )}

          <Divider sx={{ my: 1.5 }} />

          {/* ===== BORÇ KALEMLERİ ===== */}
          <SectionHeader
            icon="account_balance_wallet"
            tone="amber"
            title="Borç Hareketleri"
            subtitle="Kaynağıyla birlikte borç durumu"
            typo={typo}
            right={
              <Tooltip title="Yenile">
                <span>
                  <IconButton
                    size="small"
                    onClick={onRefreshDebt}
                    disabled={debtLoading || !onRefreshDebt}
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: '10px',
                      color: theme.palette.success.main,
                      background: alpha(theme.palette.success.main, 0.1),
                      '&:hover': {
                        background: alpha(theme.palette.success.main, 0.18),
                      },
                    }}
                  >
                    {debtLoading ? (
                      <CircularProgress size={16} sx={{ color: theme.palette.success.main }} />
                    ) : (
                      <Icon sx={{ fontSize: 18 }}>refresh</Icon>
                    )}
                  </IconButton>
                </span>
              </Tooltip>
            }
          />

          {debtLoading ? (
            <MDBox sx={{ py: 2.5, display: 'grid', placeItems: 'center' }}>
              <CircularProgress size={24} />
              <MDTypography variant="caption" sx={{ mt: 1, color: 'text.secondary', ...typo.sub }}>
                Borçlar yükleniyor…
              </MDTypography>
            </MDBox>
          ) : debtErr ? (
            <MDBox
              sx={{
                p: 1.25,
                borderRadius: '16px',
                border: `1px solid ${dividerColor}`,
                background: alpha(
                  theme.palette.error.main,
                  theme.palette.mode === 'dark' ? 0.08 : 0.04
                ),
              }}
            >
              <MDTypography variant="button" fontWeight="bold" sx={{ color: errColor }}>
                {debtErr}
              </MDTypography>
              <MDTypography variant="caption" sx={{ color: 'text.secondary', ...typo.sub }}>
                Tekrar denemek için “Yenile”ye bas.
              </MDTypography>
            </MDBox>
          ) : !debtSummary ||
            !Array.isArray(debtSummary.allItems) ||
            debtSummary.allItems.length === 0 ? (
            <EmptyState
              title="Borç kalemi yok"
              subtitle="Bu üyeye ait ledger kaydı bulunamadı."
              theme={theme}
              dividerColor={dividerColor}
              tone="info"
            />
          ) : (
            <MDBox sx={{ display: 'grid', gap: 0.9, mt: 0.5 }}>
              {debtSummary.allItems.map((it, idx) => (
                <DebtRow
                  key={`${it?.refType}-${it?.refId}-${idx}`}
                  it={it}
                  idx={idx}
                  typo={typo}
                  chipSx={chipSx}
                />
              ))}
            </MDBox>
          )}
        </SoftCard>

        {/* mobile menu */}
        <Menu
          anchorEl={menu.anchorEl}
          open={menuOpen}
          onClose={closeMenu}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <MenuItem
            onClick={() => {
              const p = menu.payment;
              closeMenu();
              onEditPayment?.(p);
            }}
            disabled={!canEdit}
          >
            <Icon sx={{ fontSize: 18, mr: 1 }}>edit</Icon>
            Düzenle
          </MenuItem>

          <Divider />

          <MenuItem
            onClick={() => {
              const p = menu.payment;
              closeMenu();
              onDeletePayment?.(p);
            }}
            disabled={!canDelete}
            sx={{ color: theme.palette.error.main }}
          >
            <Icon sx={{ fontSize: 18, mr: 1 }}>delete</Icon>
            Sil
          </MenuItem>
        </Menu>
      </MDBox>
    </TabPanel>
  );
}
